import { FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import {
  Business,
  Bolt,
  BarChart,
  Category,
  ContactPhone,
  Description,
  Gavel,
  Groups,
  Inventory,
  Language,
  Person,
  Repeat,
  SwapHoriz,
  Storefront
} from "@mui/icons-material";
import { TradingCounterParty } from "../../../../api/Api";
import { useUpdateCounterparty } from "../../dataHooks/counterpartyQueries";
import { MFT_ENERGY_AS, mftCompanies } from "../../../../utils/consts";
import { DK, EU, GB, JP, TR, US } from "country-flag-icons/react/3x2";
import { useThemeMode } from "../../../../theme";
import { UserNamePicker } from "../../../../components/userPicker";

type Props = {
  open: boolean;
  onClose: () => void;
  specificCounterparty: TradingCounterParty;
};

type CounterpartyFormState = {
  tradingTeam: string;
  contactPersonTradingTeam: string;
  counterpartyName: string;
  counterpartyType: string;
  counterpartyContactName: string;
  counterpartyContactEmail: string;
  tradingSettlementType: string;
  productType: string;
  venue: string;
  agreementType: string;
  transactionType: string;
  dealVolume?: number;
  unit: string;
  mandate: boolean;
  urgent: boolean;
  contractingEntity: string;
};

const buildFormState = (
  specificCounterparty: TradingCounterParty
): CounterpartyFormState => ({
  tradingTeam: specificCounterparty.tradingTeam || "",
  contactPersonTradingTeam: specificCounterparty.contactPersonTradingTeam || "",
  counterpartyName: specificCounterparty.counterpartyName || "",
  counterpartyType: specificCounterparty.counterpartyType || "",
  counterpartyContactName: specificCounterparty.counterpartyContactName || "",
  counterpartyContactEmail: specificCounterparty.counterpartyContactEmail || "",
  tradingSettlementType: specificCounterparty.tradingSettlementType || "",
  productType: specificCounterparty.productType || "",
  venue: specificCounterparty.venue || "",
  agreementType: specificCounterparty.agreementType || "",
  transactionType: specificCounterparty.transactionType || "",
  dealVolume: specificCounterparty.dealVolume,
  unit: specificCounterparty.unit || "",
  mandate: specificCounterparty.mandate ?? false,
  urgent: specificCounterparty.urgent ?? false,
  contractingEntity: specificCounterparty?.contractingEntity ?? MFT_ENERGY_AS
});

export const EditCounterpartyDialog: FC<Props> = ({
  open,
  onClose,
  specificCounterparty
}) => {
  const { mode } = useThemeMode();

  const [formData, setFormData] = useState<CounterpartyFormState>(() =>
    buildFormState(specificCounterparty)
  );

  const updateCounterparty = useUpdateCounterparty({
    onSuccess: () => onClose()
  });

  useEffect(() => {
    if (open) {
      setFormData(buildFormState(specificCounterparty));
    }
  }, [open, specificCounterparty]);

  const handleChange = <K extends keyof CounterpartyFormState>(
    key: K,
    value: CounterpartyFormState[K]
  ) => {
    setFormData((current) => ({
      ...current,
      [key]: value
    }));
  };

  const submitDisabled =
    !formData.tradingTeam.trim() ||
    !formData.contactPersonTradingTeam.trim() ||
    !formData.counterpartyName.trim() ||
    !formData.counterpartyType.trim() ||
    !formData.counterpartyContactName.trim() ||
    !formData.counterpartyContactEmail.trim() ||
    !formData.tradingSettlementType.trim() ||
    !formData.productType.trim() ||
    !formData.venue.trim() ||
    !formData.agreementType.trim() ||
    !formData.transactionType.trim();

  const handleSave = () => {
    if (submitDisabled) {
      return;
    }

    updateCounterparty.mutate({
      ...specificCounterparty,
      tradingTeam: formData.tradingTeam.trim() || undefined,
      contactPersonTradingTeam:
        formData.contactPersonTradingTeam.trim() || undefined,
      counterpartyName: formData.counterpartyName.trim() || undefined,
      counterpartyType: formData.counterpartyType.trim() || undefined,
      counterpartyContactName:
        formData.counterpartyContactName.trim() || undefined,
      counterpartyContactEmail:
        formData.counterpartyContactEmail.trim() || undefined,
      tradingSettlementType: formData.tradingSettlementType.trim() || undefined,
      productType: formData.productType.trim() || undefined,
      venue: formData.venue.trim() || undefined,
      agreementType: formData.agreementType.trim() || undefined,
      transactionType: formData.transactionType.trim() || undefined,
      dealVolume: formData.dealVolume,
      unit: formData.unit.trim() || undefined,
      mandate: formData.mandate,
      urgent: formData.urgent,
      contractingEntity: formData.contractingEntity
        ? formData.contractingEntity.trim()
        : MFT_ENERGY_AS
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Edit Trading Counterparty</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ paddingTop: 2 }}>
          {updateCounterparty.isError && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error">
                We couldn&apos;t save the counterparty changes. Please try
                again.
              </Alert>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FormControl fullWidth required>
                <InputLabel>Trading team</InputLabel>
                <Select
                  value={formData.tradingTeam}
                  onChange={(e) => handleChange("tradingTeam", e.target.value)}
                  label="Trading team"
                  startAdornment={
                    <InputAdornment position="start">
                      <Groups fontSize="small" />
                    </InputAdornment>
                  }
                >
                  {mftCompanies.map((company) => {
                    const split = company.split("MFT");

                    return (
                      <MenuItem key={company} value={company}>
                        {"MFT " + split[1]}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <TextField
                label="Contracting Entity (if applicable)"
                value={formData.contractingEntity}
                placeholder="Defaults to MFT Energy A/S"
                onChange={(e) =>
                  handleChange("contractingEntity", e.target.value)
                }
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Gavel fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />

              <UserNamePicker
                label="Contact person from MFT trading team"
                value={formData.contactPersonTradingTeam}
                onChange={(name) =>
                  handleChange("contactPersonTradingTeam", name)
                }
                required
                startIcon={<Person fontSize="small" />}
              />

              <TextField
                label="Counterparty name"
                value={formData.counterpartyName}
                onChange={(e) =>
                  handleChange("counterpartyName", e.target.value)
                }
                fullWidth
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />

              <TextField
                label="Counterparty type"
                value={formData.counterpartyType}
                onChange={(e) =>
                  handleChange("counterpartyType", e.target.value)
                }
                fullWidth
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Category fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />

              <TextField
                label="Counterparty contact name"
                value={formData.counterpartyContactName}
                onChange={(e) =>
                  handleChange("counterpartyContactName", e.target.value)
                }
                fullWidth
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <ContactPhone fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />

              <TextField
                label="Counterparty contact email"
                value={formData.counterpartyContactEmail}
                onChange={(e) =>
                  handleChange("counterpartyContactEmail", e.target.value)
                }
                fullWidth
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <ContactPhone fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />

              <TextField
                label="Type of trading/settlement"
                value={formData.tradingSettlementType}
                onChange={(e) =>
                  handleChange("tradingSettlementType", e.target.value)
                }
                fullWidth
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SwapHoriz fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                label="Product type"
                value={formData.productType}
                onChange={(e) => handleChange("productType", e.target.value)}
                fullWidth
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Inventory fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />

              <TextField
                label="Venue"
                placeholder="EEX, Trayport, ICE etc."
                value={formData.venue}
                onChange={(e) => handleChange("venue", e.target.value)}
                fullWidth
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Storefront fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />

              <TextField
                label="Agreement type"
                value={formData.agreementType}
                onChange={(e) => handleChange("agreementType", e.target.value)}
                fullWidth
                required
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Description fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />

              <FormControl fullWidth required>
                <InputLabel>Transaction type</InputLabel>
                <Select
                  value={formData.transactionType}
                  onChange={(e) =>
                    handleChange("transactionType", e.target.value)
                  }
                  label="Transaction type"
                  startAdornment={
                    <InputAdornment position="start">
                      <Repeat fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="one-off">One-off Transaction</MenuItem>
                  <MenuItem value="recurring">Recurring Transactions</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Deal Unit</InputLabel>
                <Select
                  value={formData.unit}
                  onChange={(e) => handleChange("unit", e.target.value)}
                  label="Deal Unit"
                  startAdornment={
                    <InputAdornment position="start">
                      <Language fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="DKK">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "space-between",
                        width: "64px"
                      }}
                    >
                      <Typography variant="body1">DKK</Typography>
                      <DK
                        height={12}
                        title="DKK"
                        style={{
                          border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                        }}
                      />
                    </Box>
                  </MenuItem>
                  <MenuItem value="EUR">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "space-between",
                        width: "64px"
                      }}
                    >
                      <Typography variant="body1">EUR</Typography>
                      <EU
                        height={12}
                        title="EUR"
                        style={{
                          border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                        }}
                      />
                    </Box>
                  </MenuItem>
                  <MenuItem value="GBP">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "space-between",
                        width: "64px"
                      }}
                    >
                      <Typography variant="body1">GBP</Typography>
                      <GB
                        height={12}
                        title="GBP"
                        style={{
                          border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                        }}
                      />
                    </Box>
                  </MenuItem>
                  <MenuItem value="JPY">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "space-between",
                        width: "64px"
                      }}
                    >
                      <Typography variant="body1">JPY</Typography>
                      <JP
                        height={12}
                        title="JPY"
                        style={{
                          border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                        }}
                      />
                    </Box>
                  </MenuItem>
                  <MenuItem value="TRY">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "space-between",
                        width: "64px"
                      }}
                    >
                      <Typography variant="body1">TRY</Typography>
                      <TR
                        height={12}
                        title="TRY"
                        style={{
                          border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                        }}
                      />
                    </Box>
                  </MenuItem>
                  <MenuItem value="USD">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "space-between",
                        width: "64px"
                      }}
                    >
                      <Typography variant="body1">USD</Typography>
                      <US
                        height={12}
                        title="USD"
                        style={{
                          border: `0.5px solid ${mode === "dark" ? "#404040" : "#e0e0e0"}`
                        }}
                      />
                    </Box>
                  </MenuItem>
                  <MenuItem value="MW">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "space-between",
                        width: "64px"
                      }}
                    >
                      <Typography variant="body1">MW</Typography>
                      <Bolt color="warning" />
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Expected deal volume/size"
                type="number"
                value={formData.dealVolume ?? ""}
                onChange={(e) =>
                  handleChange(
                    "dealVolume",
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <BarChart fontSize="small" />
                      </InputAdornment>
                    )
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    color="secondary"
                    checked={formData.mandate}
                    onChange={(e) => handleChange("mandate", e.target.checked)}
                  />
                }
                label="Mandate granted"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    color="secondary"
                    checked={formData.urgent}
                    onChange={(e) => handleChange("urgent", e.target.checked)}
                  />
                }
                label="Mark as urgent"
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                paddingTop: 2
              }}
            >
              <Button
                variant="text"
                onClick={onClose}
                disabled={updateCounterparty.isPending}
              >
                Cancel
              </Button>
              <Tooltip title="* fields are required" arrow placement="top">
                <span>
                  <Button
                    loading={updateCounterparty.isPending}
                    variant="contained"
                    disabled={submitDisabled}
                    onClick={handleSave}
                  >
                    Save changes
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
